import React from 'react';
import Button from 'components/Button';
import { ChatConversation } from 'types/chat';
import styles from './ChatConversationsPanel.module.scss';

export interface ChatConversationsPanelProps {
  conversations: ChatConversation[];
  loading: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
  onNewChat: () => void;
  onRefresh: () => void;
  refreshing: boolean;
  onDeleteConversation: (id: string) => void;
  deletingConversationId: string | null;
  /** Shown in the mobile overlay (e.g. close sheet) */
  showMobileClose?: boolean;
  onMobileClose?: () => void;
}

function formatConversationDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}

const ChatConversationsPanel: React.FC<ChatConversationsPanelProps> = ({
  conversations,
  loading,
  selectedId,
  onSelect,
  onNewChat,
  onRefresh,
  refreshing,
  onDeleteConversation,
  deletingConversationId,
  showMobileClose = false,
  onMobileClose,
}) => {
  return (
    <aside
      className={styles.panel}
      id="chat-conversations-panel"
      aria-label="Conversations"
    >
      <div className={styles.panelHeader}>
        <div className={styles.panelHeaderRow}>
          <h2 className={styles.panelTitle}>Conversations</h2>
          {showMobileClose && onMobileClose ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={onMobileClose}
              aria-label="Close conversations"
            >
              Close
            </Button>
          ) : null}
        </div>
        <div className={styles.panelActions}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onRefresh}
            disabled={loading || refreshing}
            aria-label="Refresh conversations"
          >
            {refreshing ? '…' : 'Refresh'}
          </Button>
          <Button type="button" variant="primary" size="sm" onClick={onNewChat}>
            New chat
          </Button>
        </div>
      </div>
      <div className={styles.list}>
        {loading ? (
          <div className={styles.listPlaceholder}>Loading…</div>
        ) : conversations.length === 0 ? (
          <div className={styles.listPlaceholder}>
            No saved conversations yet.
          </div>
        ) : (
          <ul className={styles.ul}>
            {conversations.map((c) => {
              const isSelected = c.id === selectedId;
              const label = c.title?.trim() || 'Untitled';
              const isDeleting = deletingConversationId === c.id;
              return (
                <li key={c.id} className={styles.listItem}>
                  <button
                    type="button"
                    className={
                      isSelected
                        ? `${styles.item} ${styles.itemSelected}`
                        : styles.item
                    }
                    onClick={() => onSelect(c.id)}
                  >
                    <span className={styles.itemTitle}>{label}</span>
                    <span className={styles.itemMeta}>
                      {formatConversationDate(c.updatedAt)}
                    </span>
                  </button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className={styles.deleteButton}
                    disabled={deletingConversationId !== null}
                    onClick={(e) => {
                      e.stopPropagation();
                      onDeleteConversation(c.id);
                    }}
                    aria-label={`Delete conversation ${label}`}
                  >
                    {isDeleting ? '…' : 'Delete'}
                  </Button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </aside>
  );
};

export default ChatConversationsPanel;
